/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminOverviewPage.tsx (v3 — rich)
 * ============================================================
 */

import {
  Users, Home, Users2, Wallet, Package, MessageSquare, Bell,
  ChefHat, Activity, Flame, DollarSign, ShoppingCart, Store,
  UserPlus, Home as HomeIc,
} from 'lucide-react';
import {
  useAdminStats, useAdminGrowth, useAdminFinance, useAdminContent, useAdminEngagement,
} from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

export default function AdminOverviewPage() {
  const { data: stats } = useAdminStats();
  const { data: growth } = useAdminGrowth(30);
  const { data: finance } = useAdminFinance();
  const { data: content } = useAdminContent();
  const { data: engagement } = useAdminEngagement();

  if (!stats) return <div className="text-slate-400 text-sm">Loading overview…</div>;

  const tpv30Formatted = finance ? formatMoney(finance.totalTPV30d) : '—';
  const tpv7Formatted  = finance ? formatMoney(finance.totalTPV7d)  : '—';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-xl font-bold">Command Center</h1>
        <p className="text-xs text-slate-400 mt-0.5">Real-time pulse across all homes.</p>
      </div>

      {/* KPI row */}
      <section>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat icon={Users}      label="Users"          value={stats.users.total.toLocaleString()} sub={`${stats.users.activeWeek} active this week`} accent="sky" />
          <Stat icon={Home}       label="Homes"          value={stats.homes.total.toLocaleString()} sub={`+${stats.homes.new30d} in 30d`}              accent="emerald" />
          <Stat icon={DollarSign} label="TPV 30d"        value={tpv30Formatted}      sub={`${tpv7Formatted} last 7d`}                accent="amber" />
          <Stat icon={Activity}   label="MAU"            value={(engagement?.mau ?? 0).toLocaleString()} sub={`${engagement?.dau ?? 0} DAU · ${engagement?.wau ?? 0} WAU`} accent="rose" />
        </div>
      </section>

      {/* Activity row */}
      <section>
        <h2 className="text-sm font-semibold text-slate-300 mb-2">Activity totals</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat icon={Wallet}        label="Transactions"    value={stats.activity.finance_transactions.toLocaleString()} accent="sky"     small />
          <Stat icon={Package}       label="Pantry items"    value={stats.activity.pantry_items.toLocaleString()}         accent="emerald" small />
          <Stat icon={MessageSquare} label="Community posts" value={stats.activity.community_posts.toLocaleString()}      accent="rose"    small />
          <Stat icon={Bell}          label="Notifs 24h"      value={stats.activity.notifications_24h.toLocaleString()}    accent="amber"   small />
          <Stat icon={Users2}        label="Communities"     value={stats.communities.total.toLocaleString()}              accent="purple"  small />
        </div>
      </section>

      {/* Growth chart */}
      {growth?.points && growth.points.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-slate-300">Signups & homes — last 30 days</h2>
            <div className="flex gap-3 text-[11px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-500 rounded-sm"></span>Signups</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-sm"></span>Homes</span>
            </div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <GrowthBars points={growth.points} />
          </div>
        </section>
      )}

      {/* Finance snapshot */}
      {finance && (
        <section>
          <h2 className="text-sm font-semibold text-slate-300 mb-2">Finance pulse</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel title="Top spend categories (30d)" emoji="📂">
              <BarList items={finance.topCategories.map((c) => ({ label: c.label, value: c.totalAmount, sub: `${c.txCount} tx` }))} formatValue={formatMoney} accent="#f59e0b" />
              {finance.topCategories.length === 0 && <EmptyRow text="No transactions yet." />}
            </Panel>
            <Panel title="Top merchants (30d)" emoji="🏪">
              <BarList items={finance.topMerchants.map((m) => ({ label: m.name, value: m.totalAmount, sub: `${m.txCount} tx` }))} formatValue={formatMoney} accent="#0ea5e9" />
              {finance.topMerchants.length === 0 && <EmptyRow text="No M-Pesa merchants parsed yet." />}
            </Panel>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <MiniStat icon={ShoppingCart} label="Avg tx per home / 30d" value={finance.avgHomeMonthlyTxCount.toLocaleString()} />
            <MiniStat icon={DollarSign}   label="Avg household budget"  value={formatMoney(finance.avgHomeBudget)} />
            <MiniStat icon={Store}        label="Currencies in use"     value={String(finance.currencyMix.length)} />
          </div>
        </section>
      )}

      {/* Engagement — top streaks */}
      {engagement && engagement.topStreakHomes.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-1.5">
            <Flame className="w-3.5 h-3.5 text-orange-400" /> Home streak leaderboard
          </h2>
          <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
            {engagement.topStreakHomes.slice(0, 10).map((h, idx) => (
              <div key={h.homeId} className="p-3 flex items-center gap-3">
                <div className={cn(
                  'h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0',
                  idx === 0 ? 'bg-amber-500/20 text-amber-300' :
                  idx === 1 ? 'bg-slate-400/20 text-slate-300' :
                  idx === 2 ? 'bg-orange-700/20 text-orange-400' :
                              'bg-slate-800 text-slate-400',
                )}>#{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-100 truncate">{h.homeName}</div>
                  {h.routineName && <div className="text-[11px] text-slate-500 truncate">{h.routineName}</div>}
                </div>
                <div className="flex items-center gap-1 text-amber-300">
                  <Flame className="w-3.5 h-3.5" />
                  <span className="font-bold tabular-nums">{h.streakDays}</span>
                  <span className="text-[10px] text-slate-400">days</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Content trends */}
      {content && (
        <section>
          <h2 className="text-sm font-semibold text-slate-300 mb-2">Content trends</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel title="Trending recipes (30d)" emoji="🍴" icon={ChefHat}>
              {content.topRecipes.length === 0 ? <EmptyRow text="No recipes used yet." /> : (
                <div className="divide-y divide-slate-800">
                  {content.topRecipes.slice(0, 6).map((r) => (
                    <div key={r.recipeId} className="p-2.5 flex items-center gap-2">
                      <span className="text-base">{r.emoji ?? '🍽️'}</span>
                      <div className="flex-1 text-sm truncate">{r.name}</div>
                      <div className="text-xs text-slate-400 tabular-nums">{r.timesUsed}×</div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
            <Panel title="Most-stocked pantry items" emoji="🥫" icon={Package}>
              {content.pantryTopItems.length === 0 ? <EmptyRow text="No pantry items yet." /> : (
                <div className="divide-y divide-slate-800">
                  {content.pantryTopItems.slice(0, 6).map((p) => (
                    <div key={p.name} className="p-2.5 flex items-center gap-2">
                      <span className="text-base">{p.emoji ?? '📦'}</span>
                      <div className="flex-1 text-sm truncate capitalize">{p.name}</div>
                      <div className="text-xs text-slate-400 tabular-nums">{p.homesUsing} {p.homesUsing === 1 ? 'home' : 'homes'}</div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
        </section>
      )}

      {/* Activity feed + countries */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {engagement && engagement.recentActivity.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Recent activity</h2>
            <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800 max-h-96 overflow-y-auto">
              {engagement.recentActivity.map((a, i) => (
                <div key={`${a.kind}-${a.id}-${i}`} className="p-2.5 flex items-start gap-2">
                  <div className={cn(
                    'h-6 w-6 rounded flex items-center justify-center flex-shrink-0 mt-0.5',
                    a.kind === 'signup'            ? 'bg-sky-500/15 text-sky-400' :
                    a.kind === 'home_created'     ? 'bg-emerald-500/15 text-emerald-400' :
                    a.kind === 'community_created'? 'bg-rose-500/15 text-rose-400' :
                                                     'bg-slate-800 text-slate-400',
                  )}>
                    {a.kind === 'signup' ? <UserPlus className="w-3 h-3" /> :
                     a.kind === 'home_created' ? <HomeIc className="w-3 h-3" /> :
                     <Users2 className="w-3 h-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-200 truncate">{a.label}</div>
                    {a.subtitle && <div className="text-[10px] text-slate-500 truncate">{a.subtitle}</div>}
                  </div>
                  <div className="text-[10px] text-slate-500 flex-shrink-0 whitespace-nowrap">{formatRelative(a.at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {stats.byCountry.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Homes by country</h2>
            <div className="bg-slate-900 border border-slate-800 rounded-lg divide-y divide-slate-800">
              {stats.byCountry.map((c) => {
                const max = Math.max(...stats.byCountry.map((x) => x.homes));
                const pct = Math.round((c.homes / max) * 100);
                return (
                  <div key={c.country} className="p-3 flex items-center gap-3">
                    <div className="w-16 text-xs font-mono">{c.country}</div>
                    <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-xs tabular-nums text-slate-400 w-12 text-right">{c.homes}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

    </div>
  );
}

// ============================================================
// Components
// ============================================================

function Stat({ icon: Icon, label, value, sub, accent, small }: {
  icon: React.ComponentType<{ className?: string }>; label: string; value: string | number; sub?: string;
  accent: 'sky' | 'emerald' | 'rose' | 'amber' | 'purple'; small?: boolean;
}) {
  const accentClass = {
    sky: 'bg-sky-500/10 text-sky-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    rose: 'bg-rose-500/10 text-rose-400',
    amber: 'bg-amber-500/10 text-amber-400',
    purple: 'bg-purple-500/10 text-purple-400',
  }[accent];
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
      <div className={cn('h-8 w-8 rounded-md flex items-center justify-center', accentClass)}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={cn('mt-3 font-bold tabular-nums', small ? 'text-xl' : 'text-2xl')}>{value}</div>
      <div className="text-[11px] text-slate-400 mt-0.5 uppercase tracking-wider">{label}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Panel({ title, emoji, icon: Icon, children }: {
  title: string; emoji?: string; icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-slate-800 flex items-center gap-2">
        {emoji && <span>{emoji}</span>}
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
        <div className="text-xs font-semibold text-slate-300">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function BarList({ items, formatValue, accent }: {
  items: Array<{ label: string; value: number; sub?: string }>;
  formatValue: (v: number) => string;
  accent: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="divide-y divide-slate-800">
      {items.map((it, idx) => {
        const pct = Math.round((it.value / max) * 100);
        return (
          <div key={`${it.label}-${idx}`} className="p-2.5">
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <div className="text-sm text-slate-100 truncate">{it.label}</div>
              <div className="text-[11px] text-slate-300 tabular-nums whitespace-nowrap flex items-center gap-1">
                {formatValue(it.value)}
                {it.sub && <span className="text-slate-500">· {it.sub}</span>}
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: accent }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 flex items-center gap-3">
      <div className="h-8 w-8 rounded-md bg-slate-800 text-slate-300 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold tabular-nums truncate">{value}</div>
        <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return <div className="p-4 text-xs text-slate-500 text-center">{text}</div>;
}

function GrowthBars({ points }: { points: Array<{ date: string; signups: number; homes_created: number }> }) {
  const max = Math.max(1, ...points.map((p) => Math.max(p.signups, p.homes_created)));
  return (
    <div className="flex items-end gap-0.5 h-32">
      {points.map((p) => (
        <div key={p.date} className="flex-1 flex flex-col items-center justify-end gap-0.5 group relative">
          <div className="w-full bg-sky-500 rounded-sm min-h-[1px]" style={{ height: `${(p.signups / max) * 100}%` }} title={`${p.date}: ${p.signups} signups`} />
          <div className="w-full bg-emerald-500 rounded-sm min-h-[1px]" style={{ height: `${(p.homes_created / max) * 100}%` }} title={`${p.date}: ${p.homes_created} homes`} />
        </div>
      ))}
    </div>
  );
}

// ============================================================
// Formatting
// ============================================================

function formatMoney(minor: number): string {
  // Assume KES (minor = cents). Convert to major.
  const major = minor / 100;
  if (major >= 1_000_000) return `KSh ${(major / 1_000_000).toFixed(1)}M`;
  if (major >= 1_000)     return `KSh ${(major / 1_000).toFixed(1)}k`;
  return `KSh ${Math.round(major).toLocaleString()}`;
}

function formatRelative(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime();
  const m = Math.round(delta / 60_000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
